/**
 * Utility for detecting server-side usage limit errors (HTTP 403/429)
 * from Supabase edge functions and external APIs.
 *
 * When detected, callers should open the UsageGateModal instead of
 * showing a generic toast.error().
 */

const LIMIT_ERROR_CODES = ['LIMIT_EXCEEDED', 'BUDGET_EXCEEDED'];

const LIMIT_ERROR_PATTERNS = [
  /limit\s*reached/i,
  /limit\s*exceeded/i,
  /usage\s*limit/i,
  /quota\s*exceeded/i,
  /upgrade.*subscription/i,
];

/**
 * Check if a server response payload indicates a usage limit error.
 *
 * Works with Supabase edge function responses where `data` is the parsed
 * JSON body (e.g. `{ error, code, current, limit, tier }`).
 */
export function isUsageLimitError(data: Record<string, unknown> | null | undefined): boolean {
  if (!data) return false;

  // Check explicit error codes from our edge functions
  if (data.code && LIMIT_ERROR_CODES.includes(data.code as string)) {
    return true;
  }

  // Check error message patterns
  const errorMessage = (data.error as string) || (data.message as string) || '';
  return LIMIT_ERROR_PATTERNS.some((pattern) => pattern.test(errorMessage));
}

/**
 * Check if an HTTP status code + error message indicates a usage limit error.
 *
 * Useful for direct fetch() calls (e.g. Sage chat Cloudflare Worker).
 */
export function isUsageLimitHttpError(status: number, message: string): boolean {
  // 429 is always a rate/usage limit
  if (status === 429) return true;
  // 403 requires message pattern matching to distinguish from auth errors
  if (status === 403) {
    return LIMIT_ERROR_PATTERNS.some((pattern) => pattern.test(message));
  }
  return false;
}

/**
 * Extract usage info from a limit error response, if available.
 */
export function extractUsageFromError(data: Record<string, unknown> | null | undefined): {
  current: number;
  limit: number;
  tier: string;
} | null {
  if (!data) return null;
  if (typeof data.current === 'number' && typeof data.limit === 'number') {
    return {
      current: data.current as number,
      limit: data.limit as number,
      tier: (data.tier as string) || 'free',
    };
  }
  return null;
}
